import { BarChart3, Database, Globe, DollarSign, Server } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBillingUsageRealtime } from "@/hooks/use-api";
import type { QuotaBlock, QuotaMetricUsage } from "@/types/api";
import type { BillingUsageRealtimeResponse } from "@/types/api";

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(2)} TB`;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
  if (bytes >= 1e3) return `${(bytes / 1e3).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

const SOURCE_LABELS: Record<string, string> = {
  endpoint: "Endpoint",
  firewall: "Firewall",
  m365_email: "M365 Email",
  entra_id: "Entra ID",
  azure_activity: "Azure Activity",
  gcp_audit: "Google Cloud Audit",
  google_workspace: "Google Workspace",
  cloudtrail: "CloudTrail",
};

export default function UsagePage() {
  const { data, isLoading, isError } = useBillingUsageRealtime();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Usage</h1>
        <p className="text-sm text-muted-foreground">
          Current month resource consumption and estimated costs
          {data?.period && <span> — {data.period}</span>}
        </p>
      </div>

      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load usage data. Data will retry automatically.
        </div>
      )}

      {/* Usage summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <UsageCard
          icon={BarChart3}
          label="Events Ingested"
          value={data ? formatNumber(data.usage.events_ingested.count) : undefined}
          sub={data ? formatBytes(data.usage.events_ingested.bytes) : undefined}
          isLoading={isLoading}
        />
        <UsageCard
          icon={Globe}
          label="API Calls"
          value={data ? formatNumber(data.usage.api_calls.count) : undefined}
          isLoading={isLoading}
        />
        <UsageCard
          icon={Database}
          label="Athena Scanned"
          value={data ? formatBytes(data.usage.athena_scanned.bytes) : undefined}
          isLoading={isLoading}
        />
        <UsageCard
          icon={DollarSign}
          label="Estimated Cost"
          value={data ? formatCurrency(data.estimated_cost.total) : undefined}
          sub={data?.estimated_cost.currency}
          isLoading={isLoading}
          highlight
        />
      </div>

      {/* Quota (#185.7) — rendered only when the backend attaches a quota
          block, which happens only in enforce mode (off/observe → quota:null,
          so the card never appears pre-#330). */}
      {data?.quota && (
        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 text-lg font-semibold">Quota</h3>
            <QuotaCard quota={data.quota} />
          </CardContent>
        </Card>
      )}

      {/* Cost breakdown */}
      <Card>
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Cost Breakdown</h3>
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : data ? (
            <CostBreakdown data={data} />
          ) : (
            <p className="text-sm text-muted-foreground">No cost data available.</p>
          )}
        </CardContent>
      </Card>

      {/* Events by source */}
      <Card>
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Events by Source</h3>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : data && Object.keys(data.events_by_source).length > 0 ? (
            <SourceBreakdownTable data={data} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No per-source breakdown available.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Last updated */}
      {data?.updated_at && (
        <p className="text-xs text-muted-foreground">
          Last updated: {new Date(data.updated_at).toLocaleString()}
        </p>
      )}

    </div>
  );
}

function UsageCard({
  icon: Icon,
  label,
  value,
  sub,
  isLoading,
  highlight,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value?: string;
  sub?: string;
  isLoading?: boolean;
  highlight?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-lg p-2 ${highlight ? "bg-green-500/10" : "bg-primary/10"}`}>
          <Icon className={`h-5 w-5 ${highlight ? "text-green-500" : "text-primary"}`} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          {isLoading ? (
            <Skeleton className="mt-1 h-6 w-20" />
          ) : (
            <>
              <p className="text-xl font-bold">{value ?? "--"}</p>
              {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Human label per plan-limits metric key. */
const QUOTA_METRIC_LABELS: Record<string, string> = {
  ingest_gb_month: "Ingest",
  query_gb_month: "Query",
  storage_gb: "Storage",
  endpoint_count: "Endpoints",
};

/** Stable display order matching the response shape. */
const QUOTA_METRIC_ORDER: (keyof QuotaBlock["metrics"])[] = [
  "ingest_gb_month",
  "query_gb_month",
  "storage_gb",
  "endpoint_count",
];

function formatQuotaValue(value: number, unit: "gb" | "count"): string {
  if (unit === "count") return Math.round(value).toLocaleString();
  return `${value.toFixed(value >= 10 ? 1 : 2)} GB`;
}

function QuotaCard({ quota }: { quota: QuotaBlock }) {
  return (
    <div className="space-y-4" data-testid="quota-card">
      {QUOTA_METRIC_ORDER.map((key) => (
        <QuotaMetricRow
          key={key}
          label={QUOTA_METRIC_LABELS[key]}
          metric={quota.metrics[key]}
        />
      ))}
    </div>
  );
}

function QuotaMetricRow({
  label,
  metric,
}: {
  label: string;
  metric: QuotaMetricUsage | undefined;
}) {
  // Defensive: the backend always emits all 4 metric keys, but this is an
  // always-network-fed render — a missing key (version skew) must not crash
  // the page. Render nothing for an absent metric.
  if (!metric) return null;
  // Unmeasured (storage_gb until #329) → "Measurement pending", no bar.
  if (!metric.measured) {
    return (
      <div data-testid="quota-metric-row">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{label}</span>
          <span className="text-muted-foreground">Measurement pending</span>
        </div>
      </div>
    );
  }

  // Unlimited (Enterprise / contracted) → "Unlimited", no bar.
  if (metric.unlimited) {
    return (
      <div data-testid="quota-metric-row">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{label}</span>
          <span className="text-muted-foreground">
            {formatQuotaValue(metric.usage, metric.unit)} · Unlimited
          </span>
        </div>
      </div>
    );
  }

  // Measured + limited: real pct label, bar width clamped to 100% (F8) so an
  // over-quota metric doesn't overflow the track while the label stays honest.
  const pct = metric.pct ?? 0;
  const pctLabel = `${(pct * 100).toFixed(0)}%`;
  const barValue = Math.min(100, Math.max(0, pct * 100));
  const limitLabel =
    metric.limit !== null ? formatQuotaValue(metric.limit, metric.unit) : "";

  return (
    <div data-testid="quota-metric-row">
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">
          {formatQuotaValue(metric.usage, metric.unit)} / {limitLabel} ({pctLabel})
        </span>
      </div>
      <Progress value={barValue} className={pct >= 1 ? "[&>div]:bg-destructive" : undefined} />
    </div>
  );
}

function CostBreakdown({ data }: { data: BillingUsageRealtimeResponse }) {
  const items = [
    { label: "Ingestion", value: data.estimated_cost.ingestion },
    { label: "Storage", value: data.estimated_cost.storage },
    { label: "Athena Queries", value: data.estimated_cost.athena },
  ];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Category</TableHead>
          <TableHead className="text-right">Cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.label}>
            <TableCell className="font-medium">{item.label}</TableCell>
            <TableCell className="text-right">{formatCurrency(item.value)}</TableCell>
          </TableRow>
        ))}
        <TableRow className="border-t-2">
          <TableCell className="font-semibold">Total</TableCell>
          <TableCell className="text-right font-semibold">
            {formatCurrency(data.estimated_cost.total)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function SourceBreakdownTable({ data }: { data: BillingUsageRealtimeResponse }) {
  const sources = Object.entries(data.events_by_source).sort(
    (a, b) => b[1].count - a[1].count,
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Source</TableHead>
          <TableHead className="text-right">Events</TableHead>
          <TableHead className="text-right">Data Volume</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sources.map(([source, metrics]) => (
          <TableRow key={source}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-muted-foreground" />
                {SOURCE_LABELS[source] ?? source}
              </div>
            </TableCell>
            <TableCell className="text-right">
              {formatNumber(metrics.count)}
            </TableCell>
            <TableCell className="text-right">
              {formatBytes(metrics.bytes)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
